/**
 * Listen to connection or authentication loss effect provided by `handshake` service
 * SPEC:
 *
 * If the user is offline - the notification bar will show a constant copy
 *     "It seems you are offline. Please try to reconnect to continue creating your video"
 *
 * If the user is online  - and there is a backend problem: the notification bar will show a dynamic copy
 *     "We are sorry, something went wrong! Trying to reconnect..." or "Trying to reconnect in (seconds countdown)"
 *     The countdown moves from 5 seconds, 30 seconds, 60 seconds
 *         If after the 3 tries the connection wasn't restored - the "Reload" button will appear.
 *         The client will still try to reconnect, but without the seconds countdown
 */
import app from 'app';
import Marionette from 'backbone.marionette';
import ConnectionStatusView from 'v/ConnectionStatusView';

app.namespace.Controller.ConnectionController = Marionette.Object.extend({
  reconnectAttemptTimeouts: [1, 1, 2, 3, 5, 8, 13, 21, 34, 55],

  initialize: function () {
    this.state = new Backbone.Model({
      online: !!window.navigator.onLine,
      mode: app.CONNECTION.ESTABLISHED,
      try: null,
      timeout: null,
    });

    this.listenTo(this.state, 'change:online', this._online_changed);
    this.listenTo(this.state, 'change:mode', this._mode_changed);
    this.listenTo(this.state, 'change:try', this._try_changed);

    $(window)
      .on('online', this._windowOnlineOffline.bind(this))
      .on('offline', this._windowOnlineOffline.bind(this));

    this._handshakeExecutor = new app.namespace.Model.TimedExecutor({
      run: this._handshakeExecutor_RUN.bind(this),
      timeout: 60,
    });

    this._reconnectExecutor = new app.namespace.Model.TimedExecutor({
      run: this._reconnectExecutor_RUN.bind(this),
      timeout: this.reconnectAttemptTimeouts,
    });
  },

  _stopListening() {
    $(window).off('online offline');
    this._handshakeExecutor.stop();
    this._reconnectExecutor.stop();
  },

  _windowOnlineOffline: function ($e) {
    this.state.set('online', $e.type === 'online');
  },

  _online_changed: function (state, online) {
    if (online) {
      if (app.CONNECTION.ESTABLISHED === this.state.get('mode')) {
        // noop, happens only during initialization
      } else {
        // back to online from deploying, or
        // back to online from offline
        this.reconnect();
      }
    } else {
      // we are definitely offline no need to ping anything
      this._handshakeExecutor.stop();
      this._reconnectExecutor.stop();
      this.state.set('mode', app.CONNECTION.LOST_OFFLINE);
    }
  },

  // reflect new mode in notification region
  _mode_changed: function (/* state, mode*/) {
    app.controller.showNotification(
      new ConnectionStatusView({
        model: this.state,
      })
    );
  },

  /**
   * define new timeout
   * @param state
   * @param _try
   * @private
   */
  _try_changed: function (state, _try) {
    let timeout = null;
    if (_.isDefined(_try)) {
      if (_try < this.reconnectAttemptTimeouts.length) {
        timeout = this.reconnectAttemptTimeouts[_try];
      } else {
        timeout = _.last(this.reconnectAttemptTimeouts);
        this.state.set('mode', app.CONNECTION.LOST_SUPPORT);
      }
    }
    this.state.set('timeout', timeout);
  },

  start: function () {
    this._handshakeExecutor.start();
  },

  needToReauthenticate: function () {
    this._handshakeExecutor.stop();
    this.state.set('mode', app.CONNECTION.REAUTHENTICATE);
  },

  userWasKicked: function () {
    app.delayedSpinner.hide();
    this._stopListening();
    this.state.set('mode', app.CONNECTION.USER_WAS_KICKED);
  },

  reconnect: function () {
    this._handshakeExecutor.stop();
    this.state.set('mode', app.CONNECTION.LOST_RECONNECT);
    this.state.set('try', 0);
    this._reconnectExecutor.set('timeout', this.state.get('timeout'));
    this._reconnectExecutor.start();
  },

  _handshakeExecutor_RUN: function (loop) {
    app.api.handshake
      .get()
      .done(
        function () {
          this.state.set('mode', app.CONNECTION.ESTABLISHED);
          this.state.set('try', null);
          loop.resolve();
        }.bind(this)
      )
      .fail(
        function (jqXHR) {
          if (jqXHR.status === 401) {
            this.needToReauthenticate();
            loop.reject();
          } else if (this.state.get('online')) {
            // if we are online but server is down (deploying)
            loop.reject();
            this.reconnect();
          } else {
            // otherwise offline has his own 'silent' mode
            loop.reject();
          }
        }.bind(this)
      );
  },

  _reconnectExecutor_RUN: function (loop) {
    app.api.handshake
      .get()
      .done(
        function () {
          loop.reject();
          this.state.set('mode', app.CONNECTION.ESTABLISHED);
          this.state.set('try', null);
          this._handshakeExecutor.start();
        }.bind(this)
      )
      .fail(
        function (jqXHR) {
          if (jqXHR.status === 401) {
            this.needToReauthenticate();
            loop.reject();
          } else {
            this.state.set('try', this.state.get('try') + 1);
            this._reconnectExecutor.set('timeout', this.state.get('timeout'));
            loop.resolve();
          }
        }.bind(this)
      );
  },
});
